import React, { createContext, useContext, useState, ReactNode } from "react";
import { PaymentProviderData } from "@lib/types/Context";

interface PaymentProviderContextType {
  // Provider data
  provider: PaymentProviderData | null;
  setProvider: (provider: PaymentProviderData | null) => void;

  // Integrations
  integrations: {
    whiteLabelCustomers: PaymentProviderData["integrations"]["whiteLabelCustomers"];
    whiteLabelLeads: PaymentProviderData["integrations"]["whiteLabelLeads"];
  };

  // UI State
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Methods
  refreshProviderData: () => Promise<void>;
  updateProviderConfig: (
    config: Partial<PaymentProviderData["config"]>,
  ) => void;
  addWhiteLabelIntegration: (customerId: string) => Promise<void>;
  removeWhiteLabelIntegration: (customerId: string) => Promise<void>;
  addWhiteLabelLeadIntegration: (leadId: string) => Promise<void>;
  removeWhiteLabelLeadIntegration: (leadId: string) => Promise<void>;
}

const PaymentProviderContext = createContext<
  PaymentProviderContextType | undefined
>(undefined);

interface PaymentProviderProviderProps {
  children: ReactNode;
  providerId?: string;
}

export const PaymentProviderProvider: React.FC<
  PaymentProviderProviderProps
> = ({ children, providerId }) => {
  const [provider, setProvider] = useState<PaymentProviderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshProviderData = async () => {
    if (!providerId) return;

    setIsLoading(true);
    try {
      // API call to fetch provider data
      // const response = await fetchProviderData(providerId);
      // setProvider(response.data);
    } catch (error) {
      console.error("Error fetching provider data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateProviderConfig = (
    newConfig: Partial<PaymentProviderData["config"]>,
  ) => {
    if (provider) {
      setProvider({
        ...provider,
        config: {
          ...provider.config,
          ...newConfig,
        },
      });
    }
  };

  const addWhiteLabelIntegration = async (customerId: string) => {
    if (!provider) return;

    try {
      // API call to add white label integration
      // await addWhiteLabelIntegrationAPI(provider.id, customerId);

      setProvider({
        ...provider,
        integrations: {
          ...provider.integrations,
          whiteLabelCustomers: [
            ...provider.integrations.whiteLabelCustomers,
            customerId,
          ],
        },
      });
    } catch (error) {
      console.error("Error adding white label integration:", error);
    }
  };

  const removeWhiteLabelIntegration = async (customerId: string) => {
    if (!provider) return;

    try {
      // API call to remove white label integration
      // await removeWhiteLabelIntegrationAPI(provider.id, customerId);

      setProvider({
        ...provider,
        integrations: {
          ...provider.integrations,
          whiteLabelCustomers: provider.integrations.whiteLabelCustomers.filter(
            (id) => id !== customerId,
          ),
        },
      });
    } catch (error) {
      console.error("Error removing white label integration:", error);
    }
  };

  const addWhiteLabelLeadIntegration = async (leadId: string) => {
    if (!provider) return;

    try {
      // API call to add white label lead integration
      // await addWhiteLabelLeadIntegrationAPI(provider.id, leadId);

      setProvider({
        ...provider,
        integrations: {
          ...provider.integrations,
          whiteLabelLeads: [...provider.integrations.whiteLabelLeads, leadId],
        },
      });
    } catch (error) {
      console.error("Error adding white label lead integration:", error);
    }
  };

  const removeWhiteLabelLeadIntegration = async (leadId: string) => {
    if (!provider) return;

    try {
      // API call to remove white label lead integration
      // await removeWhiteLabelLeadIntegrationAPI(provider.id, leadId);

      setProvider({
        ...provider,
        integrations: {
          ...provider.integrations,
          whiteLabelLeads: provider.integrations.whiteLabelLeads.filter(
            (id) => id !== leadId,
          ),
        },
      });
    } catch (error) {
      console.error("Error removing white label lead integration:", error);
    }
  };

  const value: PaymentProviderContextType = {
    provider,
    setProvider,
    integrations: provider?.integrations || {
      whiteLabelCustomers: [],
      whiteLabelLeads: [],
    },
    isLoading,
    setIsLoading,
    refreshProviderData,
    updateProviderConfig,
    addWhiteLabelIntegration,
    removeWhiteLabelIntegration,
    addWhiteLabelLeadIntegration,
    removeWhiteLabelLeadIntegration,
  };

  return (
    <PaymentProviderContext.Provider value={value}>
      {children}
    </PaymentProviderContext.Provider>
  );
};

export const usePaymentProvider = (): PaymentProviderContextType => {
  const context = useContext(PaymentProviderContext);
  if (context === undefined) {
    throw new Error(
      "usePaymentProvider must be used within a PaymentProviderProvider",
    );
  }
  return context;
};
